import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { capture } from '@/lib/analytics'
import { useGlobalState } from '@/stores/globalState'
import type { Learner } from '@/types'

// ---------------------------------------------------------------------------
// Learner list
// ---------------------------------------------------------------------------

/**
 * Fetches all learners for the authenticated user.
 * The backend scopes the list via the JWT — no userId param needed.
 */
export function useLearners() {
  const user = useGlobalState((s) => s.user)

  const { data: learners = [], isLoading } = useQuery({
    queryKey: ['learners', user?.id],
    queryFn: async () => {
      const { data } = await apiClient.get<Learner[]>('/api/learners')
      return data.map(hydrateLearner)
    },
    enabled: !!user,
  })

  return { learners, isLoading }
}

// ---------------------------------------------------------------------------
// Learner bootstrap — pre-select the last-used learner on load
// ---------------------------------------------------------------------------

/**
 * Restores the active learner once the learner list resolves:
 *   - a still-valid current selection is never overridden;
 *   - otherwise the last-selected learner (persisted `activeLearnerId`) is
 *     re-selected, falling back to the first learner;
 *   - with no learners at all (first run), the add-a-learner modal opens once.
 *
 * Mounted once, high in the authenticated tree (ThreePanelLayout).
 */
export function useLearnerBootstrap() {
  const { learners, isLoading } = useLearners()
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const activeLearnerId = useGlobalState((s) => s.activeLearnerId)
  const setActiveLearner = useGlobalState((s) => s.setActiveLearner)
  const devSkipAutoSelect = useGlobalState((s) => s.devSkipAutoSelect)

  useEffect(() => {
    if (isLoading || devSkipAutoSelect) return

    // A current, still-valid selection wins — never override the user.
    if (activeLearner && learners.some((l) => l.id === activeLearner.id)) return

    // No learners yet → nothing to select. The no-learner screen
    // (AddLearnerEmptyState, rendered by ModuleRouter) owns showing the
    // add-learner modal now — bootstrap no longer force-opens the sticky
    // global flag, which could leave the modal stuck open (2026-07-14).
    if (learners.length === 0) return

    // Pre-select the last-selected learner (persisted id), else the first.
    const target = learners.find((l) => l.id === activeLearnerId) ?? learners[0]
    if (target && target.id !== activeLearner?.id) {
      setActiveLearner(target.id, target)
    }
  }, [
    isLoading,
    devSkipAutoSelect,
    learners,
    activeLearner,
    activeLearnerId,
    setActiveLearner,
  ])
}

// ---------------------------------------------------------------------------
// Single learner
// ---------------------------------------------------------------------------

export function useLearner(learnerId: string | null) {
  const { data: learner, isLoading } = useQuery({
    queryKey: ['learner', learnerId],
    queryFn: async () => {
      const { data } = await apiClient.get<Learner>(`/api/learners/${learnerId}`)
      return hydrateLearner(data)
    },
    enabled: !!learnerId,
  })

  return {
    learner: learner ?? null,
    isLoading,
    isEvaluated: learner?.evaluationComplete ?? false,
    readingStage: learner?.readingStage ?? null,
  }
}

// ---------------------------------------------------------------------------
// Create learner
// ---------------------------------------------------------------------------

/**
 * POST /api/learners — creates a learner owned by the authenticated user.
 * The backend automatically associates the learner with the user via JWT.
 */
export function useCreateLearner() {
  const queryClient = useQueryClient()
  const user = useGlobalState((s) => s.user)

  return useMutation({
    mutationFn: async (params: {
      name: string
      age: number
      grade: number
    }) => {
      const { data: learner } = await apiClient.post<Learner>('/api/learners', {
        name: params.name,
        age: params.age,
        grade: params.grade,
      })

      return hydrateLearner(learner)
    },
    onSuccess: (learner) => {
      capture('learner_created', { age: learner.age, grade: learner.grade })
      // Seed the cache with the new learner IMMEDIATELY (before the refetch
      // lands) so the list is consistent the moment the modal calls
      // setActiveLearner(newLearner). Otherwise useLearnerBootstrap runs
      // against a stale list that lacks the new learner, its "keep current
      // selection" guard fails, and it re-selects learners[0] (an old,
      // often-evaluated learner) — clobbering the new learner and bouncing
      // the parent to Books instead of the placement wizard.
      queryClient.setQueryData<Learner[]>(['learners', user?.id], (old) =>
        old ? [...old.filter((l) => l.id !== learner.id), learner] : [learner],
      )
      queryClient.invalidateQueries({ queryKey: ['learners', user?.id] })
    },
  })
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Hydrate date strings from the API into Date objects. */
function hydrateLearner(l: Learner): Learner {
  return {
    ...l,
    createdAt: new Date(l.createdAt),
    lastEvaluationDate: l.lastEvaluationDate
      ? new Date(l.lastEvaluationDate)
      : null,
  }
}
